import { useState, useEffect, useCallback } from 'react';
import { BRAND_NAME } from '../theme/brand';
import {
  Globe, Plus, Trash2, Copy, CheckCircle2, AlertCircle, Loader2, ExternalLink,
  RefreshCw, ChevronDown, ChevronUp,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { customDomainsApi } from '../api/client';

function splitHost(host) {
  const parts = host.split('.');
  if (parts.length <= 2) return { sub: '@', apex: host };
  return {
    sub: parts.slice(0, -2).join('.'),
    apex: parts.slice(-2).join('.'),
  };
}

const RECORD_REASONS = {
  CNAME: `Routes traffic for your domain to the ${BRAND_NAME} edge.`,
  TXT: 'Domain Control Validation — required before Cloudflare will issue your SSL certificate.',
};

function mapApiRecords(apiRecords) {
  return apiRecords.map(r => ({
    type: r.type,
    name: r.name,
    fullName: r.name,
    value: r.value,
    reason: RECORD_REASONS[r.type] || '',
  }));
}

export default function CustomDomainsPanel({ tenantId }) {
  const [domains, setDomains] = useState([]);
  const [domainsLoading, setDomainsLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [verifyState, setVerifyState] = useState({});  // { [id]: { state, detail } }
  const [newHost, setNewHost] = useState('');
  const [adding, setAdding] = useState(false);

  const defaultUrl = `https://story.xnotify.com/${tenantId}/<story-slug>`;

  const loadDomains = useCallback(async () => {
    setDomainsLoading(true);
    try {
      const data = await customDomainsApi.listAll();
      setDomains(data || []);
    } catch {
      // silent — panel still usable
    }
    setDomainsLoading(false);
  }, []);

  useEffect(() => { loadDomains(); }, [loadDomains]);

  const onAdd = async () => {
    const host = newHost.trim().toLowerCase();
    if (!host) { toast.error('Enter a hostname'); return; }
    if (!/^[a-z0-9.-]+\.[a-z]{2,}$/.test(host)) { toast.error('Invalid hostname'); return; }

    setAdding(true);
    try {
      await customDomainsApi.add(tenantId, host);
      setNewHost('');
      toast.success('Domain added — publish the DNS records below.');
      await loadDomains();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to add domain');
    } finally {
      setAdding(false);
    }
  };

  const onRemove = async (domain) => {
    if (!window.confirm(`Remove ${domain.hostname}?`)) return;
    try {
      await customDomainsApi.remove(tenantId, domain.id, domain.hostname);
      toast.success('Removed');
      setExpandedId(id => id === domain.id ? null : id);
      await loadDomains();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to remove domain');
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success('Copied'),
      () => toast.error('Copy failed'),
    );
  };

  const onVerify = async (domain) => {
    const records = mapApiRecords(domain.dnsRecords || []);
    const expectedCname = records.find(r => r.type === 'CNAME')?.value?.toLowerCase();
    setVerifyState(prev => ({ ...prev, [domain.id]: { state: 'checking', detail: null } }));
    try {
      const res = await fetch(
        `https://cloudflare-dns.com/dns-query?name=${encodeURIComponent(domain.hostname)}&type=CNAME`,
        { headers: { Accept: 'application/dns-json' } },
      );
      const json = await res.json();
      const answers = json?.Answer || [];
      const cname = answers.find(a => a.type === 5)?.data?.replace(/\.$/, '');
      const ok = expectedCname ? cname?.toLowerCase() === expectedCname : false;
      if (ok) {
        setVerifyState(prev => ({ ...prev, [domain.id]: { state: 'ok', detail: `CNAME → ${cname}` } }));
        toast.success('DNS record found');
      } else if (cname) {
        setVerifyState(prev => ({ ...prev, [domain.id]: { state: 'missing', detail: `CNAME points to ${cname} (expected ${expectedCname})` } }));
        toast.error('DNS points to the wrong target');
      } else {
        setVerifyState(prev => ({ ...prev, [domain.id]: { state: 'missing', detail: 'No CNAME found yet — DNS may still be propagating.' } }));
        toast.error('DNS record not visible yet');
      }
    } catch (err) {
      setVerifyState(prev => ({ ...prev, [domain.id]: { state: 'missing', detail: err.message } }));
      toast.error('Lookup failed');
    }
  };

  return (
    <div>
      {/* Default URL info */}
      <div className="rounded-lg p-3 mb-4 text-xs flex items-start gap-2"
        style={{ background: 'var(--brand-50)', color: 'var(--brand-800)', border: '1px solid var(--brand-200)' }}>
        <Globe size={14} className="mt-0.5 flex-shrink-0" />
        <div>
          <div className="font-semibold mb-0.5">Your stories are already live</div>
          <div>
            Default URL:{' '}
            <code style={{ background: '#ffffff', padding: '1px 6px', borderRadius: 4 }}>{defaultUrl}</code>
          </div>
          <div className="mt-1" style={{ opacity: 0.85 }}>
            A custom domain lets visitors reach your stories at your own branded URL.
          </div>
        </div>
      </div>

      {/* Add domain */}
      <div className="rounded-xl p-4 mb-5" style={{ border: '1px solid #e2e8f0', background: '#f8fafc' }}>
        <div className="flex items-center gap-2 mb-2">
          <Globe size={15} style={{ color: 'var(--brand-primary)' }} />
          <h3 className="text-sm font-semibold" style={{ color: '#1e293b' }}>Add a custom domain</h3>
        </div>
        <p className="text-xs mb-3" style={{ color: '#64748b' }}>
          Enter a hostname you own (e.g. <code>stories.acme.com</code>). We'll register it on Cloudflare and return the DNS records to publish at your provider.
        </p>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={newHost}
            onChange={e => setNewHost(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') onAdd(); }}
            placeholder="stories.acme.com"
            spellCheck={false}
            autoComplete="off"
            disabled={adding}
            className="flex-1 px-3 py-2 rounded-lg text-sm"
            style={{ border: '1px solid #cbd5e1', background: '#ffffff', color: '#1e293b' }}
          />
          <button
            onClick={onAdd}
            disabled={adding || !newHost.trim()}
            className="px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5 transition-colors"
            style={{
              background: (adding || !newHost.trim()) ? '#94a3b8' : 'var(--brand-primary)',
              color: '#ffffff',
              cursor: (adding || !newHost.trim()) ? 'not-allowed' : 'pointer',
            }}
          >
            {adding ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Add
          </button>
        </div>
      </div>

      {/* Domains list */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold" style={{ color: '#1e293b' }}>
          Configured Domains
          {!domainsLoading && (
            <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-bold"
              style={{ background: 'var(--brand-50)', color: 'var(--brand-primary)' }}>
              {domains.length}
            </span>
          )}
        </h3>
        <button onClick={loadDomains} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors" title="Refresh">
          <RefreshCw size={13} style={{ color: '#94a3b8' }} />
        </button>
      </div>

      {domainsLoading ? (
        <div className="flex items-center justify-center py-10 rounded-xl" style={{ border: '1px solid #e2e8f0' }}>
          <Loader2 size={20} className="animate-spin" style={{ color: 'var(--brand-primary)' }} />
        </div>
      ) : domains.length === 0 ? (
        <div className="rounded-xl p-6 text-center" style={{ border: '1px dashed #e2e8f0', background: '#ffffff' }}>
          <Globe size={32} className="mx-auto mb-2" style={{ color: '#cbd5e1' }} />
          <p className="text-sm font-medium" style={{ color: '#94a3b8' }}>No custom domains configured</p>
          <p className="text-xs mt-1" style={{ color: '#cbd5e1' }}>Add one above to get started.</p>
        </div>
      ) : (
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #e2e8f0' }}>
          {domains.map((domain, idx) => {
            const isExpanded = expandedId === domain.id;
            const records = mapApiRecords(domain.dnsRecords || []);
            const vs = verifyState[domain.id] || { state: domain.status === 'active' ? 'ok' : 'unknown', detail: null };
            const isLast = idx === domains.length - 1;

            return (
              <div key={domain.id} style={{ borderBottom: isLast ? 'none' : '1px solid #f1f5f9' }}>
                {/* Row */}
                <div className="flex items-center gap-3 px-4 py-3">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: 'var(--brand-50)' }}>
                    <Globe size={13} style={{ color: 'var(--brand-primary)' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold truncate" style={{ color: '#1e293b' }}>
                        {domain.hostname}
                      </span>
                      <DomainStatusBadge status={domain.status} sslStatus={domain.sslStatus} />
                    </div>
                    <p className="text-[11px] mt-0.5" style={{ color: '#94a3b8' }}>
                      {domain.tenantId && <span>Tenant: <code>{domain.tenantId}</code> · </span>}
                      Added {new Date(domain.createdAt || domain.addedAt || Date.now()).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <a href={`https://${domain.hostname}`} target="_blank" rel="noreferrer"
                      className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors" title="Open domain">
                      <ExternalLink size={12} style={{ color: '#94a3b8' }} />
                    </a>
                    <button onClick={() => onRemove(domain)}
                      className="p-1.5 rounded-lg hover:bg-red-50 transition-colors" title="Remove">
                      <Trash2 size={12} style={{ color: '#ef4444' }} />
                    </button>
                    <button
                      onClick={() => setExpandedId(id => id === domain.id ? null : domain.id)}
                      className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                      title={isExpanded ? 'Collapse' : 'View DNS records'}
                    >
                      {isExpanded
                        ? <ChevronUp size={14} style={{ color: '#64748b' }} />
                        : <ChevronDown size={14} style={{ color: '#64748b' }} />}
                    </button>
                  </div>
                </div>

                {/* Expanded: DNS records + verify */}
                {isExpanded && (
                  <div className="px-4 pb-4" style={{ borderTop: '1px solid #f1f5f9', background: '#fafafa' }}>
                    {records.length > 0 ? (
                      <>
                        <p className="text-xs font-semibold mt-3 mb-2" style={{ color: '#64748b' }}>DNS Records</p>
                        <div className="space-y-2 mb-3">
                          {records.map((r, i) => <DnsRow key={i} record={r} onCopy={copy} />)}
                        </div>
                        <ProviderGuide hostname={domain.hostname} records={records} />
                      </>
                    ) : (
                      <p className="text-xs mt-3" style={{ color: '#cbd5e1' }}>No DNS records on file.</p>
                    )}

                    {vs.detail && (
                      <div className="mt-3 px-3 py-2 rounded-lg text-xs flex items-start gap-2"
                        style={{
                          background: vs.state === 'ok' ? '#f0fdf4' : '#fef2f2',
                          color: vs.state === 'ok' ? '#15803d' : '#b91c1c',
                          border: '1px solid ' + (vs.state === 'ok' ? '#bbf7d0' : '#fecaca'),
                        }}>
                        {vs.state === 'ok'
                          ? <CheckCircle2 size={14} className="mt-0.5 flex-shrink-0" />
                          : <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />}
                        <span>{vs.detail}</span>
                      </div>
                    )}

                    <div className="flex items-center gap-2 mt-3">
                      <button
                        onClick={() => onVerify(domain)}
                        disabled={vs.state === 'checking'}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5"
                        style={{
                          background: vs.state === 'ok' ? '#10b981' : 'var(--brand-primary)',
                          color: '#ffffff',
                          cursor: vs.state === 'checking' ? 'wait' : 'pointer',
                        }}
                      >
                        {vs.state === 'checking'
                          ? <><Loader2 size={11} className="animate-spin" /> Checking…</>
                          : vs.state === 'ok'
                            ? <><CheckCircle2 size={11} /> Verified — re-check</>
                            : <>Verify DNS</>}
                      </button>
                      <a
                        href={`https://www.whatsmydns.net/#CNAME/${encodeURIComponent(domain.hostname)}`}
                        target="_blank" rel="noreferrer"
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5"
                        style={{ background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' }}
                      >
                        Global propagation <ExternalLink size={10} />
                      </a>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DomainStatusBadge({ status, sslStatus }) {
  const map = {
    active:   { label: 'Active',   bg: '#d1fae5', fg: '#047857' },
    pending:  { label: 'Pending',  bg: '#fef3c7', fg: '#b45309' },
    error:    { label: 'Error',    bg: '#fee2e2', fg: '#b91c1c' },
  };
  const s = map[status] || { label: status || 'Unknown', bg: '#f1f5f9', fg: '#64748b' };
  return (
    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold"
      style={{ background: s.bg, color: s.fg }}>
      {s.label}
      {sslStatus === 'active' && ' · SSL ✓'}
    </span>
  );
}

// ─── Sub-components ──────────────────────────────────────────

const PROVIDERS = [
  {
    id: 'cloudflare',
    label: 'Cloudflare',
    dashboardUrl: 'https://dash.cloudflare.com/',
    steps: (_h, recs) => [
      { title: 'Open the Cloudflare dashboard',
        body: 'Sign in at dash.cloudflare.com and click the zone (your domain) in the list.' },
      { title: 'Go to DNS → Records',
        body: 'Left sidebar → "DNS" → "Records". Click the blue "Add record" button.' },
      ...recs.map((r) => ({
        title: `Add the ${r.type} record`,
        body:
          `Type: ${r.type}\n` +
          `Name: ${r.name}\n` +
          `${r.type === 'CNAME' ? 'Target' : 'Content'}: ${r.value}\n` +
          `Proxy status: DNS only (grey cloud)  ← important for the CNAME\n` +
          `TTL: Auto`,
      })),
      { title: 'Save and wait',
        body: 'Click "Save". Records propagate within seconds on Cloudflare. Then click "Verify DNS".' },
    ],
  },
  {
    id: 'godaddy',
    label: 'GoDaddy',
    dashboardUrl: 'https://dcc.godaddy.com/control/portfolio',
    steps: (h, recs) => [
      { title: 'Open Domain Portfolio',
        body: `Sign in at godaddy.com → "My Products" → find your domain (${splitHost(h).apex}) → click "DNS".` },
      { title: 'Go to DNS Management',
        body: 'You\'ll see a "Records" table. Click "Add New Record".' },
      ...recs.map((r) => ({
        title: `Add the ${r.type} record`,
        body:
          `Type: ${r.type}\n` +
          `Name: ${r.name}                     ← GoDaddy uses the relative name\n` +
          `${r.type === 'CNAME' ? 'Value' : 'Value (TXT)'}: ${r.value}\n` +
          `TTL: 1 Hour (default)`,
      })),
      { title: 'Save changes',
        body: 'Click "Save". GoDaddy can take 5–60 minutes to propagate.' },
    ],
  },
  {
    id: 'namecheap',
    label: 'Namecheap',
    dashboardUrl: 'https://ap.www.namecheap.com/domains/list/',
    steps: (h, recs) => [
      { title: 'Open Domain List',
        body: `Sign in at namecheap.com → "Domain List" → click "Manage" next to ${splitHost(h).apex}.` },
      { title: 'Go to Advanced DNS tab',
        body: 'Top of page → "Advanced DNS" tab → "Host Records" → "Add New Record".' },
      ...recs.map((r) => ({
        title: `Add the ${r.type} record`,
        body:
          `Type: ${r.type === 'CNAME' ? 'CNAME Record' : 'TXT Record'}\n` +
          `Host: ${r.name}\n` +
          `${r.type === 'CNAME' ? 'Target' : 'Value'}: ${r.value}\n` +
          `TTL: Automatic`,
      })),
      { title: 'Save and verify',
        body: 'Click the green checkmark to save. Propagation: usually 30 min on Namecheap.' },
    ],
  },
  {
    id: 'route53',
    label: 'AWS Route 53',
    dashboardUrl: 'https://us-east-1.console.aws.amazon.com/route53/v2/hostedzones',
    steps: (h, recs) => [
      { title: 'Open Route 53 Hosted Zones',
        body: `AWS Console → Route 53 → "Hosted zones" → click ${splitHost(h).apex}.` },
      { title: 'Create record',
        body: 'Click "Create record". Use the Quick create form for each record.' },
      ...recs.map((r) => ({
        title: `Add the ${r.type} record`,
        body:
          `Record name: ${r.name}\n` +
          `Record type: ${r.type}\n` +
          `Value: ${r.value}${r.type === 'TXT' ? '   ← Route 53 wraps TXT values in quotes for you' : ''}\n` +
          `TTL: 300\n` +
          `Routing policy: Simple`,
      })),
      { title: 'Save',
        body: 'Click "Create records". Route 53 propagates within ~60 seconds.' },
    ],
  },
  {
    id: 'google',
    label: 'Google Domains / Squarespace',
    dashboardUrl: 'https://domains.squarespace.com/',
    steps: (h, recs) => [
      { title: 'Open DNS settings',
        body: `Sign in at Squarespace → select ${splitHost(h).apex} → "DNS Settings" → "Custom records".` },
      ...recs.map((r) => ({
        title: `Add the ${r.type} record`,
        body: `Host: ${r.name}\nType: ${r.type}\nData: ${r.value}\nTTL: 1 hour`,
      })),
      { title: 'Save changes',
        body: 'Click "Save". Propagation usually under 15 minutes.' },
    ],
  },
  {
    id: 'other',
    label: 'Other / Generic',
    dashboardUrl: '',
    steps: (_h, recs) => [
      { title: 'Find the DNS / Zone editor',
        body: 'Most providers call it "DNS Records", "DNS Zone Editor", "Manage DNS", or "Advanced DNS".' },
      ...recs.map((r) => ({
        title: `Add a ${r.type} record`,
        body:
          `Record type: ${r.type}\n` +
          `Host / Name / Subdomain: ${r.name}\n` +
          `Target / Value / Points to: ${r.value}\n` +
          `TTL: leave default (300–3600 seconds)\n` +
          `${r.type === 'CNAME' ? 'Make sure proxy/CDN is OFF for this CNAME.' : 'Do not add quotes around TXT values manually.'}`,
      })),
      { title: 'Save and wait for propagation',
        body: 'Click "Verify DNS" here after a few minutes — it queries Cloudflare 1.1.1.1 directly.' },
    ],
  },
];

function ProviderGuide({ hostname, records }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState('cloudflare');

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="mt-4 px-3 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5"
        style={{ background: 'var(--brand-50)', color: 'var(--brand-primary)', border: '1px solid var(--brand-200)' }}
      >
        <Globe size={12} />
        How to add these records on your DNS provider
      </button>
    );
  }

  const provider = PROVIDERS.find(p => p.id === active) || PROVIDERS[0];
  const steps = provider.steps(hostname, records);

  return (
    <div className="mt-4 rounded-xl overflow-hidden" style={{ border: '1px solid #e2e8f0' }}>
      <div
        className="flex items-center justify-between px-3 py-2"
        style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}
      >
        <div className="flex items-center gap-2 text-xs font-semibold" style={{ color: '#1e293b' }}>
          <Globe size={12} style={{ color: 'var(--brand-primary)' }} />
          How to add these records
        </div>
        <button
          onClick={() => setOpen(false)}
          className="text-xs px-2 py-0.5 rounded hover:bg-gray-200"
          style={{ color: '#64748b' }}
        >
          Hide
        </button>
      </div>

      <div className="flex flex-wrap gap-1 px-3 py-2" style={{ background: '#ffffff', borderBottom: '1px solid #e2e8f0' }}>
        {PROVIDERS.map(p => (
          <button
            key={p.id}
            onClick={() => setActive(p.id)}
            className="px-3 py-1 rounded-md text-xs font-medium transition-colors"
            style={{
              background: active === p.id ? 'var(--brand-primary)' : '#f1f5f9',
              color: active === p.id ? '#ffffff' : '#475569',
            }}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="px-4 py-3" style={{ background: '#ffffff' }}>
        {provider.dashboardUrl && (
          <a
            href={provider.dashboardUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs font-semibold mb-3"
            style={{ color: 'var(--brand-primary)' }}
          >
            Open {provider.label} dashboard <ExternalLink size={11} />
          </a>
        )}
        <ol className="space-y-2.5 list-decimal pl-5">
          {steps.map((s, i) => (
            <li key={i} className="text-xs" style={{ color: '#1e293b' }}>
              <div className="font-semibold mb-1">{s.title}</div>
              <pre
                className="whitespace-pre-wrap p-2 rounded-md text-[11px]"
                style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  color: '#475569',
                  fontFamily: '"SF Mono", Menlo, Consolas, monospace',
                  margin: 0,
                }}
              >{s.body}</pre>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

function DnsRow({ record, onCopy }) {
  const showFullHint = record.name !== record.fullName;
  return (
    <div className="rounded-lg p-3" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
      <p className="text-xs mb-2" style={{ color: '#64748b' }}>{record.reason}</p>
      <div className="grid gap-1.5 items-center" style={{ gridTemplateColumns: '60px 1fr auto' }}>
        <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>Type</span>
        <code className="text-xs" style={{ color: '#1e293b' }}>{record.type}</code>
        <span />

        <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>Name</span>
        <code className="text-xs break-all" style={{ color: '#1e293b' }}>{record.name}</code>
        <button onClick={() => onCopy(record.name)} className="p-1 rounded hover:bg-gray-200" title="Copy">
          <Copy size={11} style={{ color: '#64748b' }} />
        </button>

        <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>Value</span>
        <code className="text-xs break-all" style={{ color: '#1e293b' }}>{record.value}</code>
        <button onClick={() => onCopy(record.value)} className="p-1 rounded hover:bg-gray-200" title="Copy">
          <Copy size={11} style={{ color: '#64748b' }} />
        </button>
      </div>

      {showFullHint && (
        <div className="flex items-center gap-1.5 mt-2 text-[11px]" style={{ color: '#94a3b8' }}>
          <span>Some DNS providers want the full name:</span>
          <code className="text-[11px]" style={{ color: '#64748b' }}>{record.fullName}</code>
          <button onClick={() => onCopy(record.fullName)} className="p-0.5 rounded hover:bg-gray-200" title="Copy full name">
            <Copy size={10} style={{ color: '#94a3b8' }} />
          </button>
        </div>
      )}
    </div>
  );
}

function VerifyPill({ state }) {
  const map = {
    unknown:  { label: 'Awaiting DNS',   bg: '#fef3c7', fg: '#b45309' },
    checking: { label: 'Checking…',      bg: 'var(--brand-100)', fg: 'var(--brand-primary-active)' },
    ok:       { label: 'DNS verified ✓', bg: '#d1fae5', fg: '#047857' },
    missing:  { label: 'DNS not found',  bg: '#fee2e2', fg: '#b91c1c' },
  };
  const s = map[state];
  return (
    <span
      className="px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
      style={{ background: s.bg, color: s.fg }}
    >
      {s.label}
    </span>
  );
}
